import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Pencil, Trash2, UserRound } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await api.get("/users");
    setUsers(data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing({ email: "", name: "", agency_name: "", password: "", partner_discount_percent: "", contracts_enabled: false }); setOpen(true); };
  const openEdit = (u) => { setEditing({ ...u, password: "", partner_discount_percent: u.partner_discount_percent == null ? "" : u.partner_discount_percent }); setOpen(true); };

  const save = async () => {
    const pdp = editing.partner_discount_percent;
    const partnerVal = (pdp === "" || pdp === null || pdp === undefined) ? null : Number(pdp);
    try {
      if (editing.id) {
        await api.put(`/users/${editing.id}`, {
          name: editing.name, agency_name: editing.agency_name,
          password: editing.password || undefined,
          partner_discount_percent: partnerVal,
          contracts_enabled: !!editing.contracts_enabled,
        });
      } else {
        await api.post("/users", {
          email: editing.email, name: editing.name,
          agency_name: editing.agency_name, password: editing.password,
          partner_discount_percent: partnerVal,
          contracts_enabled: !!editing.contracts_enabled,
        });
      }
      toast.success("Commerciale salvato");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Eliminare ${u.email}?`)) return;
    await api.delete(`/users/${u.id}`);
    toast.success("Eliminato");
    load();
  };

  return (
    <div className="p-6 lg:p-10 max-w-4xl mx-auto">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-2">
        <h1 className="text-3xl font-extrabold text-navy tracking-tight">Commerciali</h1>
        <Button onClick={openNew} data-testid="add-agent-button" className="gap-2 font-bold"><Plus size={18} /> Nuovo commerciale</Button>
      </div>
      <p className="text-muted-foreground mb-8">Crea e gestisci gli account dei commerciali.</p>

      <div className="space-y-3">
        {users.length === 0 && (
          <div className="border border-dashed border-border rounded-2xl p-16 text-center bg-white">
            <UserRound className="mx-auto text-muted-foreground mb-3" size={40} />
            <p className="text-navy font-bold">Nessun commerciale</p>
          </div>
        )}
        {users.map((u) => (
          <div key={u.id} data-testid={`agent-row-${u.id}`} className="flex items-center justify-between gap-4 bg-white border border-border rounded-xl p-4">
            <div className="min-w-0">
              <p className="font-bold text-navy">{u.name || "—"}</p>
              <p className="text-sm text-muted-foreground truncate">{u.email}{u.agency_name ? ` · ${u.agency_name}` : ""}{u.partner_discount_percent != null ? ` · sconto partner ${u.partner_discount_percent}%` : ""}{u.contracts_enabled ? " · contratti abilitati" : ""}</p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <button onClick={() => openEdit(u)} data-testid={`edit-agent-${u.id}`} className="text-muted-foreground hover:text-primary transition-colors duration-200"><Pencil size={16} /></button>
              <button onClick={() => remove(u)} data-testid={`delete-agent-${u.id}`} className="text-muted-foreground hover:text-destructive transition-colors duration-200"><Trash2 size={16} /></button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing?.id ? "Modifica commerciale" : "Nuovo commerciale"}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={editing.email} disabled={!!editing.id} data-testid="agent-email-input"
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Nome</Label>
                <Input value={editing.name} data-testid="agent-name-input" onChange={(e) => setEditing({ ...editing, name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Agenzia</Label>
                <Input value={editing.agency_name} data-testid="agent-agency-input" onChange={(e) => setEditing({ ...editing, agency_name: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>{editing.id ? "Nuova password (opzionale)" : "Password"}</Label>
                <Input type="password" value={editing.password} data-testid="agent-password-input" onChange={(e) => setEditing({ ...editing, password: e.target.value })} className="mt-1.5" />
              </div>
              <div>
                <Label>Sconto partner (%)</Label>
                <Input type="number" value={editing.partner_discount_percent} data-testid="agent-partner-discount-input"
                  onChange={(e) => setEditing({ ...editing, partner_discount_percent: e.target.value })} className="mt-1.5" placeholder="Vuoto = usa sconto generale" />
                <p className="text-xs text-muted-foreground mt-1">Commissione riservata a questa agenzia. Vuoto = sconto generale. Un eventuale override per prodotto ha comunque la precedenza.</p>
              </div>
              <label className="flex items-center justify-between gap-4 rounded-lg border border-border p-3">
                <span>
                  <span className="block text-sm font-bold text-navy">Può generare contratti</span>
                  <span className="block text-xs text-muted-foreground">Contratto ufficiale, caricamento firmato e sezione Scadenze.</span>
                </span>
                <Switch checked={!!editing.contracts_enabled} data-testid="agent-contracts-switch"
                  onCheckedChange={(v) => setEditing({ ...editing, contracts_enabled: v })} />
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-agent-button" className="font-bold">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
